import React, { useEffect, useState } from "react";
import "../vendor/css/dashboard.css";
import { getAllUsers } from "../api/Users";
import { getAllDestinations } from "../api/Destination";
import { getAllHotels } from "../api/Hotels";
import { getAllInquiryDetails } from "../api/InquiryDetails";
import { getAllPaymentDetails } from "../api/PaymentDetails";
import { getAllTravelPartner } from "../api/TravelPartner";
import { getAllPrefferPartner } from "../api/PrefferPartner";
import { getAllClientTestimonial } from "../api/ClientTestimonial";
import { getAllVacationBeingHere } from "../api/VacationBeingHere";
import { getAllAdminUsers } from "../api/AdminUser";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalDestinations: 0,
    totalHotels: 0,
    totalInquiries: 0,
    totalPayments: 0,
    totalTravelPartners: 0,
    totalPrefferPartners: 0,
    totalClientTestimonials: 0,
    totalVacationBeingHere: 0,
    totalAdminUsers: 0,
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          users,
          destinations,
          hotels,
          inquiries,
          payments,
          travelPartners,
          prefferPartners,
          clientTestimonials,
          vacationBeingHere,
          adminUsers,
        ] = await Promise.all([
          getAllUsers(),
          getAllDestinations(),
          getAllHotels(),
          getAllInquiryDetails(),
          getAllPaymentDetails(),
          getAllTravelPartner(),
          getAllPrefferPartner(),
          getAllClientTestimonial(),
          getAllVacationBeingHere(),
          getAllAdminUsers(),
        ]);

        setStats({
          totalUsers: users?.users?.length || 0,
          totalDestinations: destinations?.destinations?.length || 0,
          totalHotels: hotels?.hotels?.length || 0,
          totalInquiries: inquiries?.inquiryDetails?.length || 0,
          totalPayments: payments?.paymentDetails?.length || 0,
          totalTravelPartners:
            travelPartners?.travelPartner?.length ||
            travelPartners?.length ||
            0,
          totalPrefferPartners:
            prefferPartners?.prefferPartners?.length ||
            prefferPartners?.length ||
            0,
          totalClientTestimonials:
            clientTestimonials?.clientTestimonials?.length ||
            clientTestimonials?.length ||
            0,
          totalVacationBeingHere:
            vacationBeingHere?.vacationBeingHere?.length ||
            vacationBeingHere?.length ||
            0,
          totalAdminUsers: adminUsers?.users?.length || 0,
        });

        // Generate recent activities from the fetched data
        const activities = [];

        // Get latest users (assuming they have createdAt or similar)
        if (users?.users?.length > 0) {
          const latestUsers = users.users.slice(-3).reverse();
          latestUsers.forEach((user) => {
            activities.push({
              type: "user",
              message: `New user registered: ${user.name || user.email}`,
              time: user.createdAt
                ? new Date(user.createdAt).toLocaleString()
                : "Recently",
              icon: "bx bx-user-plus",
            });
          });
        }

        // Get latest destinations
        if (destinations?.destinations?.length > 0) {
          const latestDestinations = destinations.destinations
            .slice(-2)
            .reverse();
          latestDestinations.forEach((dest) => {
            activities.push({
              type: "destination",
              message: `New destination added: ${
                dest.destination_name || dest.name || dest.title
              }`,
              time: dest.createdAt
                ? new Date(dest.createdAt).toLocaleString()
                : "Recently",
              icon: "bx bx-map",
            });
          });
        }

        // Get latest hotels
        if (hotels?.hotels?.length > 0) {
          const latestHotels = hotels.hotels.slice(-2).reverse();
          latestHotels.forEach((hotel) => {
            activities.push({
              type: "hotel",
              message: `New hotel added: ${
                hotel.hotel_name || hotel.name || hotel.title
              }`,
              time: hotel.createdAt
                ? new Date(hotel.createdAt).toLocaleString()
                : "Recently",
              icon: "bx bx-hotel",
            });
          });
        }

        // Get latest inquiries
        if (inquiries?.inquiryDetails?.length > 0) {
          const latestInquiries = inquiries.inquiryDetails.slice(-2).reverse();
          latestInquiries.forEach((inquiry) => {
            activities.push({
              type: "inquiry",
              message: `New inquiry received`,
              time: inquiry.createdAt
                ? new Date(inquiry.createdAt).toLocaleString()
                : "Recently",
              icon: "bx bx-message",
            });
          });
        }

        // Sort activities by time (most recent first) and take top 6
        activities.sort((a, b) => new Date(b.time) - new Date(a.time));
        setRecentActivities(activities.slice(0, 6));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        // Fallback to mock data if API fails
        setStats({
          totalUsers: 1250,
          totalDestinations: 45,
          totalHotels: 89,
          totalInquiries: 234,
          totalPayments: 156,
          totalTravelPartners: 12,
          totalPrefferPartners: 8,
          totalClientTestimonials: 25,
          totalVacationBeingHere: 5,
          totalAdminUsers: 3,
        });
        setRecentActivities([
          {
            type: "user",
            message: "New user registered",
            time: "2 hours ago",
            icon: "bx bx-user-plus",
          },
          {
            type: "hotel",
            message: "New hotel added",
            time: "4 hours ago",
            icon: "bx bx-hotel",
          },
          {
            type: "inquiry",
            message: "New inquiry received",
            time: "6 hours ago",
            icon: "bx bx-message",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    const interval = setInterval(fetchAllData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const StatCard = ({ title, value, icon, color }) => (
    <div className={`stat-card ${color}`}>
      <div className="stat-icon">
        <i className={icon}></i>
      </div>
      <div className="stat-content">
        <h3>{value}</h3>
        <p>{title}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>Welcome to AACore Holiday Resort Admin Panel</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 style={{ fontFamily: "serif" }}>Dashboard</h1>
        <p style={{ fontFamily: "serif" }}>
          Welcome to AACore Holiday Resort Admin Panel
        </p>
      </div>

      <div className="stats-grid">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon="bx bx-user"
          color="blue"
        />
        <StatCard
          title="Destinations"
          value={stats.totalDestinations}
          icon="bx bx-map"
          color="green"
        />
        <StatCard
          title="Hotels"
          value={stats.totalHotels}
          icon="bx bx-building"
          color="orange"
        />
        <StatCard
          title="Inquiries"
          value={stats.totalInquiries}
          icon="bx bx-message-rounded"
          color="purple"
        />
        <StatCard
          title="Payments"
          value={stats.totalPayments}
          icon="bx bx-credit-card"
          color="red"
        />
        <StatCard
          title="Travel Partners"
          value={stats.totalTravelPartners}
          icon="fas fa-plane"
          color="teal"
        />
        <StatCard
          title="Preferred Partners"
          value={stats.totalPrefferPartners}
          icon="fas fa-handshake"
          color="indigo"
        />
        <StatCard
          title="Client Testimonials"
          value={stats.totalClientTestimonials}
          icon="bx bx-star"
          color="yellow"
        />
        <StatCard
          title="Vacation Being Here"
          value={stats.totalVacationBeingHere}
          icon="bx bx-sun"
          color="cyan"
        />
      </div>

      {/* <div className="dashboard-content">
        <div className="recent-activity">
          <h2>Recent Activity</h2>
          <div className="activity-list">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    <i className={activity.icon}></i>
                  </div>
                  <div className="activity-content">
                    <p>{activity.message}</p>
                    <span>{activity.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="activity-item">
                <div className="activity-content">
                  <p>No recent activities</p>
                  <span>Check back later</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <button className="action-btn">
              <i className="bx bx-plus"></i>
              Add Destination
            </button>
            <button className="action-btn">
              <i className="bx bx-plus"></i>
              Add Hotel
            </button>
            <button className="action-btn">
              <i className="bx bx-plus"></i>
              Add Partner
            </button>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;
